import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { getOrThrow, isNotFoundError, loadErrorMessage } from "shared/loadOrError";
import {
  subscribeMeetingIntelEvents,
  type MeetingIntelEvent,
} from "../meetingIntelligenceRealtime";
import { isAboutMeeting } from "../meetingEventScope";
import type {
  MeetingAnalysisRow,
  MeetingAnalysisRun,
  MeetingDecision,
  MeetingSuggestion,
  ReadAiActionItem,
  ReadAiMeeting,
  SuggestionPromoteOutcome,
} from "../meetingIntelligence.types";
import {
  actionItemOptionLabel,
  buildBulkClosePayload,
  buildClosePayload,
  buildReopenPayload,
  closeConfirmation,
  closedCount,
  closedSummary,
  isClosedActionItem,
  type ActionItemBulkCloseReply,
  type ActionItemCloseReason,
} from "../actionItemClose";
import { isOperator } from "auth/tier";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * The RAW Feathers service, used for the close/reopen writes.
 *
 * NOT the ServiceStore, for the reason Module 360's writes give: the library
 * ServiceStore swallows a server rejection and resolves undefined, and a bulk
 * close that "succeeded" over 38 refused rows is the single worst outcome this
 * feature can produce. A raw call throws, so a refusal is a refusal.
 */
interface RawService {
  create(data: object): Promise<unknown>;
  patch(id: number | string, data: object): Promise<unknown>;
}

/** Minimal user shape for resolving `closedBy` to a name. */
export interface ActionItemCloser {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Route ViewStore for the Meeting Detail view.
 *
 * Rule (web CLAUDE.md): route ViewStores are plain classes with
 * `makeAutoObservable` and do NOT extend ViewStore. The read-ai services are
 * read through the registered `read-ai-meetings` ServiceStore (see
 * serviceStores/meetingIntelligence.stores.ts). `get(id)` embeds the meeting's
 * participants[] + actionItems[], so the fetched record is held locally rather
 * than in the entity cache (a detail get is not a list page).
 *
 * The store subscribes to the Meeting Intelligence live channel and reloads the
 * record whenever THIS meeting finishes processing (e.g. after a reprocess or a
 * late transcript), so the tabs stay current without a manual refresh.
 */
class MeetingDetailStore {
  rootStore: RootStore;

  meetingId: number | null = null;
  meeting: ReadAiMeeting | null = null;
  /**
   * Per-task analysis rows (summary/happiness/topics/…) for this meeting —
   * ALL of them, `status: 'error'` included. Read `sortedAnalyses` for the
   * ok-only view the Analysis tab renders; the Pipeline tab needs both, because
   * "3 ok / 4 error" is a fact the ok-only list cannot express.
   */
  analyses: MeetingAnalysisRow[] = [];
  /** What the meeting SETTLED — recorded separately from work to be done. */
  decisions: MeetingDecision[] = [];
  /** Accepted deep-analysis runs, newest first. Who analysed this meeting, when,
   *  what they read and what they skipped. */
  runs: MeetingAnalysisRun[] = [];
  /**
   * READ.AI'S OWN LIST — suggestions, and NOT WORK.
   *
   * A separate array from `actionItems` on purpose, and never merged into it:
   * every count in this app that reports outstanding work derives from
   * `openActionItems`, so a suggestion cannot reach one by construction. That
   * is the whole ruling of 2026-09-18 — "we can use the read.ai as suggestions
   * but they shouldn't get turned into action items" — expressed as a shape
   * rather than as a filter somebody has to remember to apply.
   */
  suggestions: MeetingSuggestion[] = [];
  /** Suggestion id whose promotion is in flight, or null. */
  promotingSuggestionId: number | null = null;
  /** The result of the last promotion, for a one-line reply under the list. */
  promoteMessage: string | null = null;
  promoteError: string | null = null;
  loading = false;
  error: string | null = null;
  reprocessing = false;
  retryingAnalysis = false;
  reanalysing = false;
  savingRecording = false;
  /** A privacy change (#4028) is in flight. */
  savingPrivacy = false;
  uploadingRecording = false;
  /** 0–100 while a PUT is in flight, null otherwise. */
  uploadProgress: number | null = null;

  // ---- Action items: close with a reason ----------------------------------
  //
  // The Action Items tab defaults to OPEN items. A closed item is still
  // reachable (the toggle below), but it never renders as work outstanding —
  // which is the whole point: a list that shows 79 items when 67 of them are
  // settled teaches its reader to ignore it.

  /** Whether closed items are shown alongside the open ones. */
  showClosedActionItems = false;
  /** Item ids ticked for a bulk close. Open items only — see `toggleActionItemSelected`. */
  selectedActionItemIds: number[] = [];
  /** The items the close dialog is about. Empty when no close is in progress. */
  closeTargetIds: number[] = [];
  closeReason: ActionItemCloseReason = "done";
  closeEvidence = "";
  /** The other item a `duplicate` close points at, as the picker's string id. */
  closeDuplicateOfItemId = "";
  closing = false;
  closeError: string | null = null;
  /** Item id whose reopen is in flight, or null. */
  reopeningId: number | null = null;

  /** Staff directory for resolving `closedBy`; loaded lazily, once. */
  private closersLoaded = false;

  private unsubscribe: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-meetings` ServiceStore. */
  private get meetingsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("read-ai-meetings") ?? this.rootStore.readAiMeetingsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /** The registered `meeting-decisions` ServiceStore (read-only). */
  private get decisionsService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-decisions"
    ) as unknown as ServiceStoreType | null) ?? null;
  }

  /** The registered `meeting-analysis` ServiceStore (per-task outputs). */
  private get analysisService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-analysis"
    ) as unknown as ServiceStoreType | null) ?? null;
  }

  /** Read.ai's own list (`meeting-suggestions`) — find/get plus the one
   *  promote write. Read-only here; the write goes through `rawService`. */
  private get suggestionsService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-suggestions"
    ) as unknown as ServiceStoreType | null) ?? null;
  }

  /** The provenance read over `read_ai_analysis_runs` (find only). */
  private get runsService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-analysis-runs"
    ) as unknown as ServiceStoreType | null) ?? null;
  }

  /** The Gantry analysis queue -- used here for one verb, `requeue`. */
  private get analysisQueueService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-analysis-queue"
    ) as unknown as ServiceStoreType | null) ?? null;
  }


  /** The builtin `user` ServiceStore — the id → name directory for `closedBy`. */
  private get usersService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }

  /**
   * The raw Feathers service for a path — the close/reopen write path. See the
   * {@link RawService} docblock for why the ServiceStore is not used here.
   */
  private rawService(path: string): RawService | null {
    const app = this.rootStore.app as unknown as
      | { service?: (p: string) => RawService }
      | undefined;
    return app?.service?.(path) ?? null;
  }

  // ---- RBAC (UI gating only — the API re-enforces) ------------------------

  /**
   * operator+ — whether write controls render (reprocess, close an action item,
   * …). The same gate every other operator action on this page uses; the API
   * re-checks the effective tier on every call regardless of what renders.
   */
  get canWrite(): boolean {
    return isOperator(this.rootStore);
  }

  /**
   * Per-task analyses for this meeting, ordered for display.
   *
   * The `status === "ok"` filter used to live in `loadAnalyses`, which meant the
   * error rows were dropped on arrival and NOTHING in the app could count them.
   * The Pipeline tab reports "N ok / M error", so the raw rows are kept and the
   * filter moved here — the Analysis tab's population is unchanged.
   */
  get sortedAnalyses(): MeetingAnalysisRow[] {
    return this.analyses
      .filter((r) => r.status === "ok")
      .sort((a, b) => a.taskKey.localeCompare(b.taskKey));
  }

  setMeetingId(id: string) {
    this.meetingId = parseInt(id, 10);
  }

  // ---- Loading ------------------------------------------------------------

  async loadMeeting() {
    if (!this.meetingId) return;
    const ended = sessionMark(this.rootStore);
    this.ensureSubscribed();
    this.loading = true;
    this.error = null;
    try {
      const [meeting, analyses, decisions, runs, suggestions] = await Promise.all([
        // getOrThrow: the ServiceStore resolves undefined on a failed get, and
        // the page said "Meeting not found" over a load failure.
        getOrThrow<ReadAiMeeting>(this.meetingsService, String(this.meetingId)),
        this.loadAnalyses(),
        this.loadDecisions(),
        this.loadRuns(),
        this.loadSuggestions(),
      ]);
      // The supplementary reads swallow a sign-out into [], so the meeting can
      // still arrive here after reset(): it is the signed-out person's.
      if (ended()) return;
      runInAction(() => {
        this.meeting = meeting ?? null;
        this.analyses = analyses;
        this.decisions = decisions;
        this.runs = runs;
        this.suggestions = suggestions;
      });
      // Only after the items are in hand — the directory is fetched solely to
      // name whoever closed one, so a meeting with no closed item never pays.
      void this.loadClosersIfNeeded();
    } catch (err: unknown) {
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        // Nothing from an EARLIER load survives a failed one: the tabs beside
        // the error would read as this meeting's current record (and after a
        // route change they are another meeting's).
        this.meeting = null;
        this.analyses = [];
        this.decisions = [];
        this.runs = [];
        this.suggestions = [];
        // A 404 is an answer ("Meeting not found"), not a load failure.
        if (isNotFoundError(err)) return;
        this.error = loadErrorMessage(err, "Failed to load meeting");
      });
      console.error("Failed to load meeting:", err);
    } finally {
      // A sign-out's reset() already cleared it; the next person's load owns it now.
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Fetch the per-task analysis rows for this meeting. Returns [] on any error
   * (analyses are supplementary — a failure here must not blank the meeting).
   * The find is keyed by meetingId; the read service is staff-scoped find/get.
   *
   * ERROR ROWS ARE KEPT. They used to be filtered out here, which made the
   * error count unobservable everywhere downstream; `sortedAnalyses` applies
   * the ok-only filter for the Analysis tab instead.
   */
  private async loadAnalyses(): Promise<MeetingAnalysisRow[]> {
    if (!this.meetingId || !this.analysisService) return [];
    try {
      const res = (await this.analysisService.find({
        query: { meetingId: this.meetingId, $limit: 50 },
      })) as unknown as
        | { data?: MeetingAnalysisRow[] }
        | MeetingAnalysisRow[]
        | undefined;
      return Array.isArray(res) ? res : res?.data ?? [];
    } catch (err: unknown) {
      console.error("Failed to load meeting analyses:", err);
      return [];
    }
  }

  /**
   * Fetch what this meeting settled. Returns [] on any error, for the same
   * reason `loadAnalyses` does: decisions are supplementary, and a failure to
   * read them must not blank the meeting itself.
   *
   * Empty is the NORMAL state today — only the Gantry deep analysis writes
   * decisions, so every meeting HQ processed itself has none.
   */
  private async loadDecisions(): Promise<MeetingDecision[]> {
    if (!this.meetingId || !this.decisionsService) return [];
    try {
      const res = (await this.decisionsService.find({
        query: { meetingId: this.meetingId, $limit: 100 },
      })) as unknown as
        | { decisions?: MeetingDecision[] }
        | MeetingDecision[]
        | undefined;
      if (Array.isArray(res)) return res;
      return res?.decisions ?? [];
    } catch (err: unknown) {
      console.error("Failed to load meeting decisions:", err);
      return [];
    }
  }

  /**
   * Fetch the accepted analysis runs for this meeting — the provenance behind
   * the summary cards.
   *
   * [] on error, like the other supplementary loads: a failure to read the
   * history must not blank the meeting. Note that [] here means "no deep
   * analysis has run", which is the normal state for a meeting HQ processed
   * itself — it is NOT the same as a run that stated nothing.
   */
  private async loadRuns(): Promise<MeetingAnalysisRun[]> {
    if (!this.meetingId || !this.runsService) return [];
    try {
      const res = (await this.runsService.find({
        query: { meetingId: this.meetingId, $limit: 50 },
      })) as unknown as
        | { runs?: MeetingAnalysisRun[] }
        | MeetingAnalysisRun[]
        | undefined;
      if (Array.isArray(res)) return res;
      return res?.runs ?? [];
    } catch (err: unknown) {
      console.error("Failed to load meeting analysis runs:", err);
      return [];
    }
  }

  /**
   * Fetch Read.ai's own suggestions for this meeting.
   *
   * [] on error, like the other supplementary loads: a failure to read the
   * vendor's guesses must never blank the meeting. Note that [] is the NORMAL
   * state for a meeting ingested before 2026-09-18 — the vendor's list was
   * written straight into the action items back then — so an empty section is
   * not a gap.
   *
   * Promoted rows stay in the list by design (the API applies no default
   * filter): a suggestion somebody acted on is the most useful row on the page,
   * and dropping it would make the same ask look un-actioned and invite a
   * second promotion.
   */
  private async loadSuggestions(): Promise<MeetingSuggestion[]> {
    if (!this.meetingId || !this.suggestionsService) return [];
    try {
      const res = (await this.suggestionsService.find({
        query: { meetingId: this.meetingId, $limit: 200 },
      })) as unknown as
        | { data?: MeetingSuggestion[] }
        | MeetingSuggestion[]
        | undefined;
      // `find` answers with a bare ARRAY, like `read-ai-action-items`; the
      // `data` branch only covers a paginating future.
      return Array.isArray(res) ? res : res?.data ?? [];
    } catch (err: unknown) {
      console.error("Failed to load meeting suggestions:", err);
      return [];
    }
  }

  /** How many of Read.ai's guesses a human has turned into work. NOT a work
   *  count — it never feeds anything that reports outstanding items. */
  get promotedSuggestionCount(): number {
    return this.suggestions.filter((s) => s.promoted).length;
  }

  /**
   * Turn one suggestion into an action item.
   *
   * `alreadyPromoted: true` IS SUCCESS. It means another operator got there
   * first and this call created nothing; the item they wanted already exists.
   * Reporting that as an error would tell somebody their click failed when the
   * outcome they asked for is in place — and the API deliberately answers with
   * it rather than a 400 so the two can be told apart at all.
   *
   * The write goes through the RAW service for the reason the close path gives:
   * the library ServiceStore swallows a server rejection and resolves
   * undefined, and a promotion that "succeeded" while the server refused it is
   * the worst outcome this control can produce.
   */
  async promoteSuggestion(id: number): Promise<boolean> {
    if (this.promotingSuggestionId != null) return false;
    if (!this.canWrite) {
      runInAction(() => {
        this.promoteError =
          "Promoting a suggestion needs the operator role. Ask an administrator to change your role.";
      });
      return false;
    }
    runInAction(() => {
      this.promotingSuggestionId = id;
      this.promoteError = null;
      this.promoteMessage = null;
    });
    try {
      const service = this.rawService("meeting-suggestions");
      if (!service) {
        throw new Error("Not connected to the API — reload and try again.");
      }
      const outcome = (await service.patch(id, {
        action: "promote",
      })) as SuggestionPromoteOutcome | undefined;
      if (!outcome) {
        throw new Error(
          "The server did not confirm the promotion — nothing was created. Try again."
        );
      }
      const message = outcome.alreadyPromoted
        ? `Already promoted — action item #${outcome.actionItemId ?? "?"} already exists.`
        : `Promoted into action item #${outcome.actionItemId ?? "?"}.`;
      runInAction(() => {
        this.promoteMessage = message;
      });
      this.rootStore.notificationStore?.addSuccessNotification(message);
      // Reload so the item appears in the Action Items list and the suggestion
      // picks up its promoter stamp.
      await this.reloadMeeting();
      return true;
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Failed to promote the suggestion";
      runInAction(() => {
        if (!isSessionEnded(err)) this.promoteError = message;
      });
      if (!isSessionEnded(err)) this.rootStore.notificationStore?.addErrorNotification(message);
      console.error("Failed to promote suggestion:", err);
      return false;
    } finally {
      runInAction(() => {
        this.promotingSuggestionId = null;
      });
    }
  }

  dismissPromoteMessage() {
    this.promoteMessage = null;
    this.promoteError = null;
  }

  /** The most recent accepted run, or null if the meeting has never had one. */
  get latestRun(): MeetingAnalysisRun | null {
    return this.runs[0] ?? null;
  }

  /**
   * Did the latest run include the recording?
   *
   * THREE states, deliberately, because only two of them are a claim:
   *   'yes' / 'no'  the run stated it
   *   'not-stated'  the producer sent no `sources` field
   *   null          no run at all
   *
   * Never collapse 'not-stated' into 'no'. Doing so would have HQ answer a
   * question about the video on behalf of a producer that never spoke, which is
   * exactly the trap the API's `gaps` contract is shaped to avoid.
   */
  get latestRunVideoState(): "yes" | "no" | "not-stated" | null {
    const run = this.latestRun;
    if (!run) return null;
    // ABSENT is the only "not stated". A PRESENT array is the complete list of
    // what the run consumed (see the API's HandoffSource contract), so a
    // recording that does not appear in it was not consumed — reading that as
    // "no comment" would make the field decorative, since a producer could then
    // satisfy it by listing its transcript and never answering the question.
    if (run.sources === null) return "not-stated";
    const rec = run.sources.find((s) => s.kind === "recording");
    if (!rec) return "no";
    return rec.used ? "yes" : "no";
  }

  /** Alias used by realtime callbacks / post-action refreshes. */
  async reloadMeeting() {
    await this.loadMeeting();
  }

  // ---- Privacy (#4028) ------------------------------------------------------

  /** Whether this meeting is private. An API that predates the feature reads as public. */
  get isPrivate(): boolean {
    return this.meeting?.isPrivate === true;
  }

  /** The meeting's HQ owner or an administrator (UI gating; the API re-checks). */
  get mayChangePrivacy(): boolean {
    return this.meeting?.privacy?.mayChange === true;
  }

  /**
   * Make the meeting private or public. Through the RAW Feathers service, not
   * the ServiceStore: the store resolves a refusal to null, which would read
   * here as success — and a person told "this meeting is now private" when it
   * is not has been told the one thing this control must never get wrong. The
   * API answers with the meeting as this caller now sees it.
   */
  async setPrivacy(isPrivate: boolean): Promise<boolean> {
    if (!this.meetingId || this.savingPrivacy) return false;
    const service = this.rawService("read-ai-meetings");
    if (!service) return false;
    const ended = sessionMark(this.rootStore);
    this.savingPrivacy = true;
    try {
      const updated = (await service.patch(this.meetingId, { action: "set-privacy", isPrivate })) as ReadAiMeeting | null;
      if (ended()) return false;
      runInAction(() => {
        if (updated && typeof updated === "object" && updated.id === this.meeting?.id) {
          this.meeting = { ...this.meeting, ...updated };
        }
      });
      this.rootStore.notificationStore?.addSuccessNotification(
        isPrivate ? "This meeting is now private." : "This meeting is visible to all staff again."
      );
      return true;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to change whether this meeting is private";
      if (!isSessionEnded(err)) this.rootStore.notificationStore?.addErrorNotification(message);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.savingPrivacy = false;
        });
      }
    }
  }

  // ---- Reprocess ----------------------------------------------------------

  /**
   * Re-run the intelligence pipeline for this meeting via the `reprocess`
   * custom action (POST with `{ action: 'reprocess', id }`). On success the
   * record is reloaded so the refreshed summary / action items show.
   */
  async reprocess() {
    if (!this.meetingId) return;
    const ended = sessionMark(this.rootStore);
    this.reprocessing = true;
    try {
      await this.meetingsService?.create({
        action: "reprocess",
        id: this.meetingId,
      } as never);
      // The ServiceStore swallows a sign-out into null: no reload for it.
      if (ended()) return;
      await this.reloadMeeting();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to reprocess meeting";
        }
      });
      console.error("Failed to reprocess meeting:", err);
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.reprocessing = false;
        });
      }
    }
  }

  // ---- Retry deep analysis ------------------------------------------------

  /**
   * Send a meeting whose deep analysis FAILED back to the Gantry queue.
   *
   * `failed` used to be terminal: the banner said "will not be retried" and
   * meant it, because nothing in the API moved a meeting out of that state.
   * This is the human path back -- one PATCH, `{ action: 'requeue' }` -- after
   * which the next producer poll picks the meeting up again. The reply says
   * whether the row actually moved; a meeting that was already re-queued (or
   * re-claimed) by the time the button was pressed is reported as such rather
   * than as a success.
   */
  async retryAnalysis(): Promise<boolean> {
    if (!this.meetingId || this.retryingAnalysis) return false;
    const ended = sessionMark(this.rootStore);
    this.retryingAnalysis = true;
    try {
      const reply = (await this.analysisQueueService?.patch(this.meetingId, {
        action: "requeue",
      } as never)) as { requeued?: boolean } | undefined;
      // A sign-out resolves null here (the ServiceStore swallows it): not
      // "Nothing changed", and no reload in the next person's session.
      if (ended()) return false;
      const moved = reply?.requeued === true;
      if (moved) {
        this.rootStore.notificationStore?.addSuccessNotification(
          "Meeting sent back for deep analysis — the next producer poll will pick it up."
        );
      } else {
        this.rootStore.notificationStore?.addWarningNotification(
          "Nothing changed — this meeting is no longer in a failed state."
        );
      }
      await this.reloadMeeting();
      return moved;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to requeue the meeting";
        }
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.retryingAnalysis = false;
        });
      }
    }
  }

  // ---- Re-analysis --------------------------------------------------------

  /**
   * Run a FINISHED meeting through deep analysis again.
   *
   * Distinct from {@link retryAnalysis}, which only rescues a `failed` meeting:
   * the API refuses `requeue` on an `analyzed` row on purpose. The reason to
   * re-run a successful analysis is that the inputs changed — almost always a
   * recording attached after the fact — and the API's `reanalyse` action clears
   * the previous producer's un-acted-upon items so the review queue does not end
   * up holding the same work twice.
   *
   * Items somebody already acted on are KEPT, and the reply says how many. That
   * count is surfaced rather than swallowed: an operator who is not told will
   * find the old items beside the new ones and assume the re-run duplicated
   * everything.
   */
  async reanalyse(): Promise<boolean> {
    if (!this.meetingId || this.reanalysing) return false;
    const ended = sessionMark(this.rootStore);
    this.reanalysing = true;
    try {
      const reply = (await this.analysisQueueService?.patch(this.meetingId, {
        action: "reanalyse",
      } as never)) as
        | { requeued?: boolean; clearedItems?: number; keptItems?: number }
        | undefined;
      // As in retryAnalysis: a swallowed sign-out is not "Nothing changed".
      if (ended()) return false;
      const moved = reply?.requeued === true;
      if (moved) {
        const cleared = reply?.clearedItems ?? 0;
        const kept = reply?.keptItems ?? 0;
        const keptNote = kept
          ? ` ${kept} item${kept === 1 ? "" : "s"} already acted on were kept.`
          : "";
        this.rootStore.notificationStore?.addSuccessNotification(
          `Queued for re-analysis — the next producer poll will pick it up. ` +
            `Cleared ${cleared} un-actioned item${cleared === 1 ? "" : "s"}.${keptNote}`
        );
      } else {
        this.rootStore.notificationStore?.addWarningNotification(
          "Nothing changed — the meeting may be claimed by a producer right now."
        );
      }
      await this.reloadMeeting();
      return moved;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to queue the re-analysis";
        }
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.reanalysing = false;
        });
      }
    }
  }

  // ---- Attached recording -------------------------------------------------

  /**
   * Point this meeting at a recording HQ did not get from Read.ai, or clear it.
   *
   * A URL rather than an upload: `documents` caps a file at 25 MB, holds no
   * video mime type, and buffers the whole body in memory — none of which
   * survives a real meeting recording. HQ proxies this URL to the producer
   * through the same `/source/recording` endpoint that serves Read.ai's, so the
   * producer needs no knowledge that a human intervened.
   *
   * Pass an empty string to clear. A wrong recording is worse than none, so
   * taking one back is always allowed.
   */
  async setRecording(url: string, note?: string): Promise<boolean> {
    if (!this.meetingId || this.savingRecording) return false;
    const ended = sessionMark(this.rootStore);
    this.savingRecording = true;
    try {
      await this.meetingsService?.patch(this.meetingId, {
        action: "set-recording",
        recordingUrl: url,
        recordingNote: note ?? "",
      } as never);
      // The ServiceStore swallows a sign-out into null: no "Recording
      // attached" toast for the next person, and no reload.
      if (ended()) return false;
      this.rootStore.notificationStore?.addSuccessNotification(
        url.trim()
          ? "Recording attached — it will be served to the producer on the next run."
          : "Recording cleared."
      );
      await this.reloadMeeting();
      return true;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save the recording";
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = message;
      });
      if (!isSessionEnded(err)) this.rootStore.notificationStore?.addErrorNotification(message);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.savingRecording = false;
        });
      }
    }
  }

  /** Whether this meeting has an operator-attached recording. */
  get hasAttachedRecording(): boolean {
    return Boolean(this.meeting?.manualRecordingUrl);
  }

  /** Whether the recording is archived in HQ's own blob storage. */
  get hasStoredRecording(): boolean {
    return Boolean(this.meeting?.recordingFileId);
  }

  /** The registered `meeting-recording-uploads` RPC pair. */
  private get recordingUploadsService(): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(
      "meeting-recording-uploads"
    ) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Upload a recording file for this meeting: mint a SAS ticket, PUT the bytes
   * STRAIGHT TO BLOB STORAGE (they never transit the API — which is what lifts
   * the old 25 MB documents cap), then commit, which verifies the blob and
   * links it as the meeting's recording.
   *
   * XMLHttpRequest rather than fetch for the one thing fetch still lacks:
   * upload progress events. A multi-hundred-MB PUT with no progress reads as a
   * hang, and the first thing a person does with a hang is retry it.
   */
  async uploadRecording(file: File): Promise<boolean> {
    if (!this.meetingId || this.uploadingRecording) return false;
    const ended = sessionMark(this.rootStore);
    this.uploadingRecording = true;
    this.uploadProgress = 0;
    let fileId: string | null = null;
    try {
      const ticket = (await this.recordingUploadsService?.create({
        meetingId: this.meetingId,
        name: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      } as never)) as unknown as {
        fileId: string;
        upload: { url: string; headers?: Record<string, string> };
      };
      // The ServiceStore can swallow a server rejection and resolve undefined,
      // so the RESULT is the contract, not the absence of a throw. Without
      // this check a refused ticket fell through to a misleading generic
      // error; with it, the two actionable refusals are named.
      if (!ticket?.upload?.url) {
        throw new Error(
          "The server refused the upload ticket — the file may exceed the size limit, " +
            "or file storage (FILES_AZURE_CONNECTION_STRING) is not configured. " +
            "You can still attach the recording as a URL."
        );
      }
      fileId = ticket.fileId;

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", ticket.upload.url);
        for (const [k, v] of Object.entries(ticket.upload.headers ?? {})) {
          xhr.setRequestHeader(k, v);
        }
        xhr.setRequestHeader(
          "Content-Type",
          file.type || "application/octet-stream"
        );
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && !ended()) {
            runInAction(() => {
              this.uploadProgress = Math.round((e.loaded / e.total) * 100);
            });
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Storage answered ${xhr.status}`));
        xhr.onerror = () => reject(new Error("Upload failed — network error"));
        xhr.send(file);
      });
      // The PUT is a raw XHR the session guard never sees: signed out while it
      // ran, the commit must not go out under whoever signs in next.
      if (ended()) return false;

      const commit = (await this.recordingUploadsService?.patch(this.meetingId, {
        action: "commit",
        fileId,
      } as never)) as unknown as { committed?: boolean } | undefined;
      // Same reasoning as the ticket check: a swallowed commit rejection must
      // not become a success toast over a blob stranded as `pending`.
      if (commit?.committed !== true) {
        throw new Error(
          "The upload finished but the server did not confirm the recording — it was not linked. Retry the upload."
        );
      }

      this.rootStore.notificationStore?.addSuccessNotification(
        "Recording uploaded and stored — it will be served to the producer from HQ's own storage."
      );
      await this.reloadMeeting();
      return true;
    } catch (err: unknown) {
      // A sign-out: the ticket and commit reads swallow it into null, which
      // lands here as a plain "server refused" Error — so ask the marker, not
      // only the error. No toast, and no abandon call: it would go out under
      // whoever signs in next. The GC sweep collects the pending row.
      if (isSessionEnded(err) || ended()) return false;
      // Take the pending row back out so a retry mints a fresh ticket instead
      // of leaving an orphan for the GC sweep. Best-effort — the commit never
      // ran, so nothing points at it.
      if (fileId) {
        void this.recordingUploadsService
          ?.patch(this.meetingId, { action: "abandon", fileId } as never)
          .catch(() => undefined);
      }
      const message =
        err instanceof Error ? err.message : "Failed to upload the recording";
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = message;
      });
      if (!isSessionEnded(err)) this.rootStore.notificationStore?.addErrorNotification(message);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.uploadingRecording = false;
          this.uploadProgress = null;
        });
      }
    }
  }

  // ---- Action items: open / closed ----------------------------------------

  /** Every action item on the loaded meeting, closed ones included. */
  get actionItems(): ReadAiActionItem[] {
    return this.meeting?.actionItems ?? [];
  }

  /**
   * The items that are still WORK. Everything in the app that reports
   * outstanding action items for this meeting counts this, not `actionItems` —
   * a closed item has been settled and must never be rendered as outstanding.
   */
  get openActionItems(): ReadAiActionItem[] {
    return this.actionItems.filter((item) => !isClosedActionItem(item));
  }

  get closedActionItems(): ReadAiActionItem[] {
    return this.actionItems.filter((item) => isClosedActionItem(item));
  }

  /** What the Action Items tab renders — open only unless the toggle is on. */
  get visibleActionItems(): ReadAiActionItem[] {
    return this.showClosedActionItems ? this.actionItems : this.openActionItems;
  }

  get openActionItemCount(): number {
    return this.openActionItems.length;
  }

  get closedActionItemCount(): number {
    return this.closedActionItems.length;
  }

  setShowClosedActionItems(show: boolean) {
    this.showClosedActionItems = show;
  }

  // ---- Bulk selection -----------------------------------------------------

  /**
   * The selection AS IT APPLIES — the ticked ids intersected with the items
   * that are still open. Derived rather than pruned on write, so a reload that
   * closes an item drops it from the selection without anything having to
   * remember to.
   */
  get selectedOpenActionItemIds(): number[] {
    const open = new Set(this.openActionItems.map((i) => i.id));
    return this.selectedActionItemIds.filter((id) => open.has(id));
  }

  get selectedActionItemCount(): number {
    return this.selectedOpenActionItemIds.length;
  }

  isActionItemSelected(id: number): boolean {
    return this.selectedActionItemIds.includes(id);
  }

  toggleActionItemSelected(id: number) {
    this.selectedActionItemIds = this.selectedActionItemIds.includes(id)
      ? this.selectedActionItemIds.filter((existing) => existing !== id)
      : [...this.selectedActionItemIds, id];
  }

  /** Tick / untick every OPEN item currently on screen. */
  setAllActionItemsSelected(selected: boolean) {
    this.selectedActionItemIds = selected
      ? this.visibleActionItems
          .filter((item) => !isClosedActionItem(item))
          .map((item) => item.id)
      : [];
  }

  /** True when every open item on screen is ticked (and there is one). */
  get allVisibleActionItemsSelected(): boolean {
    const selectable = this.visibleActionItems.filter(
      (item) => !isClosedActionItem(item),
    );
    return (
      selectable.length > 0 &&
      selectable.every((item) => this.isActionItemSelected(item.id))
    );
  }

  clearActionItemSelection() {
    this.selectedActionItemIds = [];
  }

  // ---- Close dialog state -------------------------------------------------

  /** Open the close dialog for ONE item. */
  startCloseActionItem(id: number) {
    this.resetCloseForm();
    this.closeTargetIds = [id];
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "CloseActionItemsDialog",
    });
  }

  /**
   * Open the close dialog for every ticked open item. One reason for the whole
   * set, one call — this is the path that clears a 67-item backlog, and a loop
   * of single closes would leave it half-cleared on the first refusal.
   */
  startBulkCloseActionItems() {
    const ids = this.selectedOpenActionItemIds;
    if (ids.length === 0) return;
    this.resetCloseForm();
    this.closeTargetIds = ids;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "CloseActionItemsDialog",
    });
  }

  private resetCloseForm() {
    this.closeTargetIds = [];
    this.closeReason = "done";
    this.closeEvidence = "";
    this.closeDuplicateOfItemId = "";
    this.closeError = null;
  }

  setCloseReason(reason: ActionItemCloseReason) {
    this.closeReason = reason;
  }

  setCloseEvidence(value: string) {
    this.closeEvidence = value;
  }

  setCloseDuplicateOfItemId(value: string) {
    this.closeDuplicateOfItemId = value;
  }

  /** True when the dialog has everything the API needs. A reason is required. */
  get isCloseFormValid(): boolean {
    return this.closeTargetIds.length > 0 && !!this.closeReason;
  }

  get isBulkClose(): boolean {
    return this.closeTargetIds.length > 1;
  }

  /** "Close 38 items as already done?" — the question the dialog asks. */
  get closeConfirmationText(): string {
    return closeConfirmation(this.closeTargetIds.length, this.closeReason);
  }

  /**
   * Candidates for the "duplicate of" reference: every OTHER item on this
   * meeting. Restricted to this meeting because that is the set a reviewer can
   * actually see — pointing at an item from a meeting nobody has open would be
   * a reference they cannot check.
   */
  get duplicateOptions(): { id: number; label: string }[] {
    const targets = new Set(this.closeTargetIds);
    return this.actionItems
      .filter((item) => !targets.has(item.id))
      .map((item) => ({ id: item.id, label: actionItemOptionLabel(item) }));
  }

  // ---- Close / reopen writes ----------------------------------------------

  /**
   * Apply the close locally before the server answers, and hand back the
   * previous rows so a refusal can put them straight back.
   *
   * Optimism is worth it here precisely because of the bulk case: repainting 38
   * rows only after a round trip reads as a hang, and the operator's next move
   * on a hang is to press the button again.
   */
  private applyOptimisticFields(
    ids: number[],
    fields: Partial<ReadAiActionItem>,
  ): ReadAiActionItem[] | null {
    const meeting = this.meeting;
    if (!meeting?.actionItems) return null;
    const previous = meeting.actionItems.slice();
    const targets = new Set(ids);
    meeting.actionItems = meeting.actionItems.map((item) =>
      targets.has(item.id) ? { ...item, ...fields } : item,
    );
    return previous;
  }

  private restoreActionItems(previous: ReadAiActionItem[] | null) {
    if (previous && this.meeting) this.meeting.actionItems = previous;
  }

  /**
   * Submit the close dialog: ONE patch for a single item, one bulk POST for
   * several. Wired to the route dialog's `onSubmit`.
   *
   * The tier is re-checked inside the mutator — `canWrite` is the render gate,
   * not the write gate — and the API re-enforces regardless.
   */
  async submitClose(): Promise<boolean> {
    const ids = this.closeTargetIds.slice();
    if (this.closing || ids.length === 0) return false;
    if (!this.canWrite) {
      runInAction(() => {
        this.closeError =
          "Closing an action item needs the operator role. Ask an administrator to change your role.";
      });
      return false;
    }
    const meetingId = this.meetingId;
    if (meetingId == null) return false;

    const duplicateOfItemId = Number(this.closeDuplicateOfItemId);
    const input = {
      reason: this.closeReason,
      evidence: this.closeEvidence,
      duplicateOfItemId: Number.isFinite(duplicateOfItemId)
        ? duplicateOfItemId
        : null,
    };

    runInAction(() => {
      this.closing = true;
      this.closeError = null;
    });

    const previous = this.applyOptimisticFields(ids, {
      status: "closed",
      closedReason: input.reason,
      closedAt: new Date().toISOString(),
    });

    try {
      const service = this.rawService("read-ai-action-items");
      if (!service) {
        throw new Error("Not connected to the API — reload and try again.");
      }

      let closed = ids.length;
      if (ids.length === 1) {
        await service.patch(ids[0], buildClosePayload(input));
      } else {
        const reply = (await service.create(
          buildBulkClosePayload(meetingId, ids, input),
        )) as ActionItemBulkCloseReply | undefined;
        // Gate on the RESULT, not merely on the absence of a throw: the bulk
        // close is all-or-nothing and its per-item outcomes are the only
        // statement of what actually moved.
        if (!reply) {
          throw new Error(
            "The server did not confirm the close — nothing was changed. Try again.",
          );
        }
        closed = closedCount(reply, ids.length);
      }

      this.rootStore.notificationStore?.addSuccessNotification(
        `Closed ${closed} action item${closed === 1 ? "" : "s"}.`,
      );
      runInAction(() => {
        this.selectedActionItemIds = [];
        this.closeTargetIds = [];
      });
      await this.reloadMeeting();
      return true;
    } catch (err: unknown) {
      // A sign-out ended the call: reset() has emptied the meeting, and
      // `previous` is the signed-out person's rows. Restoring them would hand
      // them to whoever signs in next, so this check comes before the rollback.
      if (isSessionEnded(err)) return false;
      // Put the rows back: a refused close that still reads as closed on screen
      // is worse than no close at all.
      runInAction(() => {
        this.restoreActionItems(previous);
        this.closeError =
          err instanceof Error && err.message
            ? err.message
            : "Failed to close the action item";
      });
      // No toast: the dialog's route throws closeError, so the dialog stays
      // open and shows it in its footer. A toast as well said it twice.
      console.error("Failed to close action items:", err);
      return false;
    } finally {
      runInAction(() => {
        this.closing = false;
      });
    }
  }

  /** Put a closed item back to open; the API clears every close field. */
  async reopenActionItem(id: number): Promise<boolean> {
    if (this.reopeningId != null) return false;
    if (!this.canWrite) return false;
    runInAction(() => {
      this.reopeningId = id;
      this.closeError = null;
    });
    const previous = this.applyOptimisticFields([id], {
      status: "open",
      closedReason: null,
      closedAt: null,
      closedBy: null,
      closedEvidence: null,
      duplicateOfItemId: null,
    });
    try {
      const service = this.rawService("read-ai-action-items");
      if (!service) {
        throw new Error("Not connected to the API — reload and try again.");
      }
      await service.patch(id, buildReopenPayload());
      this.rootStore.notificationStore?.addSuccessNotification(
        "Action item reopened.",
      );
      await this.reloadMeeting();
      return true;
    } catch (err: unknown) {
      // Before the rollback AND the toast: `previous` is the signed-out
      // person's rows, and "Failed to reopen" would be shown to the next one.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.restoreActionItems(previous);
        this.closeError =
          err instanceof Error && err.message
            ? err.message
            : "Failed to reopen the action item";
      });
      this.rootStore.notificationStore?.addErrorNotification(
        this.closeError ?? "Failed to reopen the action item",
      );
      console.error("Failed to reopen action item:", err);
      return false;
    } finally {
      runInAction(() => {
        this.reopeningId = null;
      });
    }
  }

  // ---- Closed-item display ------------------------------------------------

  /** Staff directory the `closedBy` label resolves through (may be empty). */
  get closers(): ActionItemCloser[] {
    return (this.usersService?.entities ?? []) as unknown as ActionItemCloser[];
  }

  /**
   * Who closed an item. Degrades to `User #7` rather than inventing a name, and
   * to "" when the API sent no `closedBy` at all.
   */
  closedByLabel(item: ReadAiActionItem): string {
    if (item.closedBy == null) return "";
    const user = this.closers.find((u) => u.id === item.closedBy);
    if (!user) return `User #${item.closedBy}`;
    const name = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || user.email || `User #${item.closedBy}`;
  }

  /** "Already done · Alice Smith · 17/09/2026" for a closed row. */
  closedSummaryFor(item: ReadAiActionItem): string {
    return closedSummary(item, this.closedByLabel(item));
  }

  /**
   * Fetch the staff directory, but ONLY once this meeting actually has a closed
   * item to attribute. Every meeting would otherwise pay for a 500-row user
   * find to render a label that almost none of them need.
   */
  private async loadClosersIfNeeded() {
    if (this.closersLoaded) return;
    if (!this.actionItems.some((item) => item.closedBy != null)) return;
    this.closersLoaded = true;
    try {
      await this.usersService?.find({ query: { $limit: 500 } });
    } catch (err: unknown) {
      // Non-fatal — the label falls back to `User #id`.
      console.error("Failed to load users for the closed-by label:", err);
    }
  }

  // ---- Realtime -----------------------------------------------------------

  private ensureSubscribed() {
    if (this.unsubscribe) return;
    // Shared realtime bus (src/realtime) — auth is sourced internally from the
    // AuthStore. This subscription used to be silently inert because it was
    // made with no token; that state is no longer expressible.
    this.unsubscribe = subscribeMeetingIntelEvents(
      this.rootStore,
      (event: MeetingIntelEvent) => {
        // Both "the analysis landed" events. `meeting-processed` is HQ's own
        // pass (still emitted where `readAi.analysisAuthority` is 'hq');
        // `meeting-analyzed` is a producer's stored result push — the ONLY
        // one that fires once Gantry is the authority, so listening for the
        // first alone left an open Meeting Detail showing "not analysed" over
        // a meeting whose items, analyses and memory had already arrived.
        if (this.meetingId == null) return;
        if (this.isAboutThisMeeting(event)) void this.reloadMeeting();
      }
    );
  }

  /**
   * Does a Meeting Intelligence event change what this page shows? The shared
   * rule (meetingEventScope.ts `isAboutMeeting`, which the Review tab asks
   * too), against this meeting's items' assignments.
   */
  private isAboutThisMeeting(event: MeetingIntelEvent): boolean {
    if (this.meetingId == null) return false;
    const assignmentIds = new Set(
      this.actionItems
        .filter((item) => item.assignmentId != null)
        .map((item) => String(item.assignmentId))
    );
    return isAboutMeeting(event, this.meetingId, assignmentIds);
  }

  /** Tear down the realtime subscription + clear state (call on route exit). */
  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    runInAction(() => {
      this.meetingId = null;
      this.meeting = null;
      this.analyses = [];
      this.decisions = [];
      this.error = null;
      // A selection and a half-filled close form belong to the meeting that was
      // open; carrying either into the next one is how a bulk close lands on
      // the wrong rows.
      this.selectedActionItemIds = [];
      this.showClosedActionItems = false;
      this.resetCloseForm();
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.meetingId = null;
    this.meeting = null;
    this.analyses = [];
    this.decisions = [];
    this.runs = [];
    this.suggestions = [];
    this.promotingSuggestionId = null;
    this.promoteMessage = null;
    this.promoteError = null;
    this.loading = false;
    this.error = null;
    this.reprocessing = false;
    this.retryingAnalysis = false;
    this.reanalysing = false;
    this.savingRecording = false;
    this.savingPrivacy = false;
    this.uploadingRecording = false;
    this.uploadProgress = null;
    this.showClosedActionItems = false;
    this.selectedActionItemIds = [];
    this.closeTargetIds = [];
    this.closeReason = "done";
    this.closeEvidence = "";
    this.closeDuplicateOfItemId = "";
    this.closing = false;
    this.closeError = null;
    this.reopeningId = null;
    this.closersLoaded = false;
    this.unsubscribe = null;
  }
}

export default MeetingDetailStore;
